import { useEffect, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { api, describeError } from '@/lib/api';
import {
  NO_CAMPAIGN_LABEL,
  NO_CAMPAIGN_SOURCE,
  type AcquisitionSummary,
} from '@/lib/acquisition';
import { CardTable, THead, TBody, Tr, Th, Td } from '@/components/CardTable';

const PERIODS = [7, 30, 90] as const;
type Period = (typeof PERIODS)[number];

interface Props {
  /** Chamado com a fonte da linha clicada (`-` para "sem campanha"). */
  onPickSource: (source: string) => void;
}

/**
 * "Cadastros por origem": quantos tenants cada combinação de fonte, meio e
 * campanha trouxe no período. É onde se compara um criativo com outro.
 */
export function AcquisitionSummaryCard({ onPickSource }: Props) {
  const [period, setPeriod] = useState<Period>(30);
  const [summary, setSummary] = useState<AcquisitionSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const from = new Date(Date.now() - period * 24 * 60 * 60 * 1000).toISOString();
    api
      .get<AcquisitionSummary>('/platform/tenants/acquisition-summary', { params: { from } })
      .then(({ data }) => {
        if (cancelled) return;
        setSummary(data);
        setError(null);
      })
      .catch((err) => !cancelled && setError(describeError(err)));
    return () => { cancelled = true; };
  }, [period]);

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <TrendingUp size={18} />
        <div className="card-title" style={{ flex: 1 }}>Cadastros por origem</div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="acq-period" className="sr-only">Período</label>
          <select
            id="acq-period"
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value) as Period)}
          >
            {PERIODS.map((p) => (
              <option key={p} value={p}>Últimos {p} dias</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {summary && summary.rows.length === 0 && (
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>Nenhum cadastro no período.</p>
      )}

      {summary && summary.rows.length > 0 && (
        <div className="table-scroll">
          <CardTable aria-label="Cadastros por origem">
            <THead>
              <Tr>
                <Th>Fonte</Th>
                <Th>Meio</Th>
                <Th>Campanha</Th>
                <Th style={{ textAlign: 'right' }}>Cadastros</Th>
              </Tr>
            </THead>
            <TBody>
              {summary.rows.map((row) => (
                <Tr key={`${row.source}|${row.medium}|${row.campaign}`}>
                  <Td heading>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => onPickSource(row.source ?? NO_CAMPAIGN_SOURCE)}
                      title="Filtrar a lista por esta fonte"
                    >
                      {row.source ?? NO_CAMPAIGN_LABEL}
                    </button>
                  </Td>
                  <Td label="Meio" className="muted">{row.medium ?? '—'}</Td>
                  <Td label="Campanha" className="mono">{row.campaign ?? '—'}</Td>
                  <Td label="Cadastros" style={{ textAlign: 'right', fontWeight: 600 }}>{row.count}</Td>
                </Tr>
              ))}
            </TBody>
          </CardTable>
          <p className="muted" style={{ margin: '8px 0 0', fontSize: 12 }}>
            {summary.total} {summary.total === 1 ? 'cadastro' : 'cadastros'} no período.
          </p>
        </div>
      )}
    </div>
  );
}
